import { createAppKit, type AppKit } from "@reown/appkit";
import { EthersAdapter } from "@reown/appkit-adapter-ethers";
import { ConnectionController, ConnectorController, ModalController, RouterController } from "@reown/appkit-controllers";
import UniversalProvider from "@walletconnect/universal-provider";
import {
  arbitrum,
  base,
  bsc,
  mainnet,
  polygon,
  polygonAmoy,
  sepolia,
  type AppKitNetwork,
} from "@reown/appkit/networks";
import { Interface } from "ethers";
import { RESET_WARNING, afterResetError, allowanceArrived, isRevertError, lateApproveWaitMs, needsZeroFirst } from "./allowance";
import type { MandateDraft } from "./types";

const ERC20_APPROVE = new Interface([
  "function approve(address spender, uint256 amount) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function balanceOf(address account) view returns (uint256)",
]);

export class InsufficientFundsError extends Error {
  readonly assets: string[];

  constructor(assets: string[], purpose = "activate the subscription") {
    const listed =
      assets.length <= 1
        ? assets[0] || "funds"
        : `${assets.slice(0, -1).join(", ")} and ${assets[assets.length - 1]}`;
    super(`You don't have enough ${listed} to ${purpose}. Top up or connect another wallet.`);
    this.name = "InsufficientFundsError";
    this.assets = assets;
  }
}

const NETWORKS: [AppKitNetwork, ...AppKitNetwork[]] = [
  sepolia,
  polygonAmoy,
  polygon,
  mainnet,
  bsc,
  arbitrum,
  base,
];

const ADD_CHAIN: Record<number, { chainName: string; rpcUrls: string[]; nativeCurrency: { name: string; symbol: string; decimals: number }; blockExplorerUrls: string[] }> = {
  11155111: {
    chainName: "Sepolia",
    rpcUrls: ["https://ethereum-sepolia-rpc.publicnode.com"],
    nativeCurrency: { name: "Sepolia ETH", symbol: "ETH", decimals: 18 },
    blockExplorerUrls: ["https://sepolia.etherscan.io"],
  },
  80002: {
    chainName: "Polygon Amoy",
    rpcUrls: ["https://rpc-amoy.polygon.technology"],
    nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
    blockExplorerUrls: ["https://amoy.polygonscan.com"],
  },
  137: {
    chainName: "Polygon",
    rpcUrls: ["https://polygon-rpc.com"],
    nativeCurrency: { name: "POL", symbol: "POL", decimals: 18 },
    blockExplorerUrls: ["https://polygonscan.com"],
  },
  1: {
    chainName: "Ethereum",
    rpcUrls: ["https://rpc.ankr.com/eth"],
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    blockExplorerUrls: ["https://etherscan.io"],
  },
  56: {
    chainName: "BNB Smart Chain",
    rpcUrls: ["https://bsc-dataseed.binance.org"],
    nativeCurrency: { name: "BNB", symbol: "BNB", decimals: 18 },
    blockExplorerUrls: ["https://bscscan.com"],
  },
  42161: {
    chainName: "Arbitrum",
    rpcUrls: ["https://arb1.arbitrum.io/rpc"],
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    blockExplorerUrls: ["https://arbiscan.io"],
  },
  8453: {
    chainName: "Base",
    rpcUrls: ["https://mainnet.base.org"],
    nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
    blockExplorerUrls: ["https://basescan.org"],
  },
};

const CONNECTED_WALLET_KEY = "fg-recurring-connected-wallet";

let modal: AppKit | null = null;
let appkitReady: Promise<AppKit> | null = null;
let connectInFlight: Promise<`0x${string}`> | null = null;
let connectGeneration = 0;

/** Customer account (reference) the remembered wallet belongs to. Empty means nothing is kept. */
let walletScope = "";

function storedWalletKey(): string {
  return walletScope ? `${CONNECTED_WALLET_KEY}:${walletScope}` : "";
}

function readStoredWallet(): `0x${string}` | null {
  const key = storedWalletKey();
  if (!key) return null;
  try {
    const value = localStorage.getItem(key);
    return value && /^0x[0-9a-fA-F]{40}$/.test(value) ? (value as `0x${string}`) : null;
  } catch {
    return null;
  }
}

function writeStoredWallet(address: `0x${string}` | null): void {
  const key = storedWalletKey();
  if (!key) return;
  try {
    if (address) localStorage.setItem(key, address);
    else localStorage.removeItem(key);
  } catch {
    /* a third-party iframe can block storage */
  }
}

try {
  sessionStorage.removeItem(CONNECTED_WALLET_KEY);
} catch {
  /* same as above */
}

/** Wallet the customer authorized for the current reference. */
let pageWallet: `0x${string}` | null = null;

/** A new reference starts without a wallet; the same reference gets its wallet back after a refresh. */
export function setWalletScope(scope: string | null | undefined): void {
  const next = (scope || "").trim();
  if (next === walletScope) return;
  walletScope = next;
  pageWallet = readStoredWallet();
}

function rememberWallet(address: `0x${string}`): `0x${string}` {
  pageWallet = address;
  writeStoredWallet(address);
  return address;
}

/** Address picked on this page, if the customer has connected. */
export function connectedWallet(): `0x${string}` | null {
  return pageWallet;
}

/**
 * Forget the address and ask the wallet to disconnect.
 * The call is not awaited: MetaMask may ask the customer to confirm, and that must not freeze the page.
 */
export function signOutWallet(): Promise<void> {
  pageWallet = null;
  writeStoredWallet(null);
  connectGeneration += 1;
  dismissWalletModal();
  const appkit = modal;
  if (!appkit) return Promise.resolve();
  return appkit.disconnect().catch(() => {
    /* the page has already forgotten the address */
  });
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function projectId(): string {
  const id = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID;
  if (!id) {
    throw new Error("Set VITE_WALLETCONNECT_PROJECT_ID");
  }
  return id;
}

function networkById(chainId: number): AppKitNetwork | undefined {
  return NETWORKS.find((net) => Number(net.id) === chainId);
}

function hexChainId(chainId: number): `0x${string}` {
  return `0x${chainId.toString(16)}`;
}

function providerErrorCode(error: unknown): number | undefined {
  if (!error || typeof error !== "object") return undefined;
  const rec = error as { code?: unknown; data?: { originalError?: { code?: unknown } } };
  const raw = rec.code ?? rec.data?.originalError?.code;
  return typeof raw === "number" ? raw : undefined;
}

export function getAppKit(defaultNetwork?: AppKitNetwork): AppKit {
  if (modal) return modal;
  const origin = window.location.origin;
  modal = createAppKit({
    adapters: [new EthersAdapter()],
    networks: NETWORKS,
    defaultNetwork: defaultNetwork || sepolia,
    defaultAccountTypes: { eip155: "eoa" },
    projectId: projectId(),
    metadata: {
      name: "Flowgate Recurring",
      description: "Approve recurring crypto charges",
      url: origin,
      icons: [`${origin}/logo.svg`],
    },
    themeMode: "light",
    themeVariables: {
      "--apkt-font-family": "Inter, ui-sans-serif, system-ui, sans-serif",
      "--apkt-accent": "#8b5cf6",
      "--w3m-accent": "#8b5cf6",
    },
    features: {
      analytics: false,
      email: false,
      socials: false,
      send: false,
      swaps: false,
      onramp: false,
    },
  });
  return modal;
}

function currentAddress(appkit: AppKit): `0x${string}` | null {
  const fromAccount = appkit.getAccount("eip155")?.address;
  const fromGetter = appkit.getAddress("eip155");
  const value = fromAccount || fromGetter;
  return value && value.startsWith("0x") ? (value as `0x${string}`) : null;
}

function isConnected(appkit: AppKit): boolean {
  return Boolean(appkit.getAccount("eip155")?.isConnected || appkit.getIsConnectedState() || currentAddress(appkit));
}

/** Whether this account has a live session, so a wallet action will not open the connect window. */
export function walletConnectedAs(account: string | null | undefined): boolean {
  const appkit = modal;
  if (!appkit || !account) return false;
  return Boolean(evmProvider(appkit) && isConnected(appkit) && sameAddress(currentAddress(appkit), account));
}

function evmProvider(appkit: AppKit): any {
  return appkit.getWalletProvider();
}

function sameAddress(left?: string | null, right?: string | null): boolean {
  return Boolean(left && right && left.toLowerCase() === right.toLowerCase());
}

function shortAddress(value: string): string {
  return value.length < 12 ? value : `${value.slice(0, 6)}…${value.slice(-4)}`;
}

function asAddress(value: unknown): `0x${string}` | null {
  return typeof value === "string" && value.startsWith("0x") ? (value as `0x${string}`) : null;
}

/** The account MetaMask currently has selected. AppKit keeps the previous one after an in-wallet switch. */
async function selectedAccount(provider: any): Promise<`0x${string}` | null> {
  if (!provider) return null;
  try {
    const accounts = await requestProvider(provider, "eth_accounts");
    if (!Array.isArray(accounts)) return null;
    return asAddress(accounts.find((item) => typeof item === "string" && item.startsWith("0x")));
  } catch {
    return null;
  }
}

type AccountWatch = {
  done: Promise<`0x${string}`>;
  stop: () => void;
};

function watchSelectedAccount(
  provider: any,
  accept: (address: `0x${string}`) => boolean,
  isCancelled?: () => boolean
): AccountWatch {
  let settled = false;
  let poll = 0;
  let giveUp = 0;
  let stop = () => {};
  const done = new Promise<`0x${string}`>((resolve, reject) => {
    const finish = (error?: Error, address?: `0x${string}`) => {
      if (settled) return;
      settled = true;
      if (poll) window.clearInterval(poll);
      if (giveUp) window.clearTimeout(giveUp);
      provider?.removeListener?.("accountsChanged", onChange);
      if (address) resolve(address);
      else reject(error || new Error("Wallet connection cancelled"));
    };
    stop = () => finish(new Error("Wallet connection cancelled"));
    const check = () => {
      if (settled) return;
      if (isCancelled?.()) {
        finish(new Error("Wallet connection cancelled"));
        return;
      }
      void selectedAccount(provider).then((address) => {
        if (address && accept(address)) finish(undefined, address);
      });
    };
    const onChange = () => check();
    provider?.on?.("accountsChanged", onChange);
    poll = window.setInterval(check, 400);
    giveUp = window.setTimeout(() => finish(new Error("Switch the account in your wallet and try again")), 180_000);
    check();
  });
  done.catch(() => undefined);
  return { done, stop: () => stop() };
}

async function promptPayerAccount(
  provider: any,
  payer: string,
  onStatus?: (message: string) => void,
  isCancelled?: () => boolean
): Promise<`0x${string}`> {
  onStatus?.(`Switch to ${shortAddress(payer)} in your wallet. That address pays this subscription.`);
  dismissWalletModal();
  try {
    await requestProvider(provider, "wallet_requestPermissions", [{ eth_accounts: {} }]);
  } catch (error) {
    if (providerErrorCode(error) === 4001) throw new Error("Wallet connection cancelled");
    /* WalletConnect has no account picker. Wait for a manual switch instead. */
  }
  if (isCancelled?.()) throw new Error("Wallet connection cancelled");
  const selected = await selectedAccount(provider);
  if (sameAddress(selected, payer)) return selected!;
  const watch = watchSelectedAccount(provider, (address) => sameAddress(address, payer), isCancelled);
  try {
    return await watch.done;
  } finally {
    watch.stop();
  }
}

async function requestProvider(provider: any, method: string, params?: unknown): Promise<any> {
  if (!provider || typeof provider.request !== "function") {
    throw new Error("Wallet provider is not ready");
  }
  return provider.request(params === undefined ? { method } : { method, params });
}

async function readChainId(provider: any): Promise<number | null> {
  try {
    const raw = await requestProvider(provider, "eth_chainId");
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

async function waitForChain(provider: any, chainId: number, timeoutMs = 25_000): Promise<boolean> {
  const started = Date.now();
  while (Date.now() - started < timeoutMs) {
    if ((await readChainId(provider)) === chainId) return true;
    await new Promise((resolve) => window.setTimeout(resolve, 350));
  }
  return (await readChainId(provider)) === chainId;
}

async function addAndSwitchChain(provider: any, chainId: number, label: string): Promise<void> {
  const hex = hexChainId(chainId);
  const extra = ADD_CHAIN[chainId];
  try {
    await requestProvider(provider, "wallet_switchEthereumChain", [{ chainId: hex }]);
  } catch (error) {
    const code = providerErrorCode(error);
    if (code === 4001) {
      throw new Error(`Switch the wallet to ${label} and try again`);
    }
    if (extra && (code === 4902 || code === -32603 || String((error as Error)?.message || "").toLowerCase().includes("unrecognized"))) {
      await requestProvider(provider, "wallet_addEthereumChain", [
        {
          chainId: hex,
          chainName: extra.chainName,
          nativeCurrency: extra.nativeCurrency,
          rpcUrls: extra.rpcUrls,
          blockExplorerUrls: extra.blockExplorerUrls,
        },
      ]);
    } else {
      throw error;
    }
  }
}

async function ensureChain(appkit: AppKit, provider: any, network: AppKitNetwork): Promise<void> {
  const chainId = Number(network.id);
  const label = network.name || `chain ${chainId}`;
  if ((await readChainId(provider)) === chainId) return;

  try {
    await appkit.switchNetwork(network, { throwOnFailure: false });
  } catch {
    /* WalletConnect MetaMask often ignores AppKit switch; fall through to wallet_switchEthereumChain */
  }
  if (await waitForChain(provider, chainId, 4_000)) return;

  await addAndSwitchChain(provider, chainId, label);
  if (!(await waitForChain(provider, chainId))) {
    throw new Error(`Wallet stayed on the wrong network. Switch to ${label} in MetaMask, then retry.`);
  }
}

function walletModalOpen(): boolean {
  return Boolean(document.querySelector("w3m-modal")?.classList.contains("open"));
}

/** Drop a connect overlay left open after an approval. It covers the page and swallows clicks. */
export function dismissWalletModal(): void {
  document.querySelectorAll("style[data-w3m]").forEach((node) => node.remove());
  document.body.style.overflow = "";
  document.body.style.touchAction = "";
  document.body.style.overscrollBehavior = "";
  try {
    ModalController.close();
  } catch {
    /* AppKit is not booted yet */
  }
  const el = document.querySelector("w3m-modal");
  el?.classList.remove("open");
  if (el) (el as unknown as { open?: boolean }).open = false;
}

type Eip155Adapter = {
  universalProvider?: {
    on?: (event: string, listener: (uri: string) => void) => void;
    session?: { topic?: string } | null;
  };
  connectors?: Array<{ id?: string }>;
  setUniversalProvider?: (provider: unknown) => Promise<unknown>;
};

let walletConnectReady: Promise<void> | null = null;

function walletConnectTopic(appkit: AppKit): string {
  const adapter = (appkit as unknown as { getAdapter?: (namespace: string) => Eip155Adapter | undefined }).getAdapter?.("eip155");
  return adapter?.universalProvider?.session?.topic || "";
}

function walletConnectListed(): boolean {
  return ConnectorController.state.connectors.some((connector) => connector.id === "walletConnect");
}

/**
 * AppKit adds WalletConnect only after every installed wallet answers getProvider().
 * That call can sit forever, so the row and Search never appear. Register the connector first.
 */
function ensureWalletConnectConnector(appkit: AppKit): Promise<void> {
  if (walletConnectListed()) return Promise.resolve();
  if (walletConnectReady) return walletConnectReady;
  walletConnectReady = (async () => {
    const client = appkit as unknown as {
      getAdapter?: (namespace: string) => Eip155Adapter | undefined;
      getUniversalProvider?: () => Promise<unknown>;
    };
    const adapter = client.getAdapter?.("eip155");
    if (!adapter?.setUniversalProvider) return;
    if (!adapter.universalProvider) {
      // AppKit's own provider: a second UniversalProvider on the same storage restores the same sessions twice.
      let provider = client.getUniversalProvider ? await client.getUniversalProvider() : undefined;
      if (!provider) {
        const origin = window.location.origin;
        const own = await UniversalProvider.init({
          projectId: projectId(),
          metadata: {
            name: "Flowgate Recurring",
            description: "Approve recurring crypto charges",
            url: origin,
            icons: [`${origin}/logo.svg`],
          },
        });
        own.on("display_uri", (uri: string) => {
          ConnectionController.setUri(uri);
        });
        provider = own;
      }
      if (!adapter.universalProvider) await adapter.setUniversalProvider(provider);
    }
    const connector = adapter.connectors?.find((item) => item.id === "walletConnect");
    if (connector && !ConnectorController.state.allConnectors.some((item) => item.id === "walletConnect")) {
      ConnectorController.setConnectors([connector as never]);
    }
    if (!walletConnectListed()) ConnectorController.setFilterByNamespace("eip155");
  })().catch((error) => {
    walletConnectReady = null;
    throw error;
  });
  return walletConnectReady;
}

function shadowQueryAll(root: ParentNode, selector: string, found: Element[] = []): Element[] {
  root.querySelectorAll("*").forEach((el) => {
    if (el.matches(selector)) found.push(el);
    if (el.shadowRoot) shadowQueryAll(el.shadowRoot, selector, found);
  });
  return found;
}

/** The wallet list reads connectors only when it renders, so a late WalletConnect row needs a redraw. */
function refreshConnectorList(): void {
  const modalEl = document.querySelector("w3m-modal");
  if (!modalEl) return;
  for (const el of shadowQueryAll(modalEl.shadowRoot || modalEl, "w3m-connector-list")) {
    (el as unknown as { requestUpdate?: () => void }).requestUpdate?.();
  }
}

async function ensureAppKit(defaultNetwork?: AppKitNetwork): Promise<AppKit> {
  if (appkitReady) return appkitReady;
  appkitReady = (async () => {
    const appkit = getAppKit(defaultNetwork);
    // ready() asks installed wallets for a provider and can sit forever.
    void appkit.ready().catch(() => {
      /* WalletConnect is registered separately and must not wait for this */
    });
    void ensureWalletConnectConnector(appkit).catch(() => {
      /* the click path awaits this and surfaces the error */
    });
    return appkit;
  })();
  try {
    return await appkitReady;
  } catch (error) {
    appkitReady = null;
    throw error;
  }
}

/**
 * WalletConnect setup and the wallet catalogue can both stall on stored sessions or blocked requests.
 * Open with whatever is ready, and fail with a retry instead of an endless "Opening wallet…".
 */
async function openConnectModal(appkit: AppKit): Promise<void> {
  const registering = ensureWalletConnectConnector(appkit).then(
    () => true,
    () => false
  );
  const registered = await Promise.race([registering, sleep(15000).then(() => false)]);
  if (!registered) {
    void registering.then((ok) => {
      if (!ok || !walletModalOpen()) return;
      refreshConnectorList();
    });
  }
  let timedOut = false;
  const opening = appkit.open({ view: "Connect" }).then(() => {
    if (timedOut) dismissWalletModal();
  });
  const opened = await Promise.race([opening.then(() => true), sleep(8000).then(() => false)]);
  if (!opened) {
    timedOut = true;
    throw new Error("The wallet window did not open. Check your connection and try again.");
  }
}

function waitForProvider(appkit: AppKit): Promise<any> {
  const current = evmProvider(appkit);
  if (current) return Promise.resolve(current);
  return new Promise((resolve, reject) => {
    const timeout = window.setTimeout(() => {
      unsub();
      reject(new Error("Wallet provider is not ready"));
    }, 15_000);
    const unsub = appkit.subscribeProviders((state) => {
      const next = state["eip155"] || evmProvider(appkit);
      if (next) {
        window.clearTimeout(timeout);
        unsub();
        resolve(next);
      }
    });
  });
}

function acceptedAddress(address: string | null | undefined, connected: boolean, ignored: string): address is `0x${string}` {
  return Boolean(connected && address?.startsWith("0x") && address.toLowerCase() !== ignored);
}

async function waitForConnection(
  appkit: AppKit,
  ignoreAddress: string | null | undefined,
  generation: number,
  holdAddress?: string | null,
  untilClose = false
): Promise<`0x${string}`> {
  const ignored = ignoreAddress?.toLowerCase() || "";
  const hold = holdAddress?.toLowerCase() || "";
  const fresh = (address: string | null | undefined, connected: boolean): address is `0x${string}` =>
    acceptedAddress(address, connected, ignored) && address.toLowerCase() !== hold;
  const existing = currentAddress(appkit);
  if (!untilClose && !hold && fresh(existing, isConnected(appkit))) return existing;

  return new Promise((resolve, reject) => {
    let settled = false;
    let sawOpen = walletModalOpen();
    // Set once the customer opens an installed wallet or pairs a new WalletConnect session.
    // After that the held address counts as a pick.
    let picked = false;
    const startTopic = walletConnectTopic(appkit);
    const pickable = (address: string | null | undefined, connected: boolean): address is `0x${string}` =>
      fresh(address, connected) || (picked && acceptedAddress(address, connected, ignored));
    let closeTimer = 0;
    let poll = 0;
    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      if (closeTimer) window.clearTimeout(closeTimer);
      if (poll) window.clearInterval(poll);
      try {
        unsubAcc();
      } catch {
        /* unsubscribe can throw if the modal already tore the subscription down */
      }
      try {
        unsubState();
      } catch {
        /* same as above */
      }
      fn();
    };
    const cancelIfClosed = () => {
      if (closeTimer) return;
      closeTimer = window.setTimeout(() => {
        closeTimer = 0;
        if (generation !== connectGeneration || !walletModalOpen()) {
          if (generation === connectGeneration) {
            const address = currentAddress(appkit);
            if (fresh(address, isConnected(appkit))) {
              rememberWallet(address);
              finish(() => resolve(address));
              return;
            }
          }
          finish(() => reject(new Error("Wallet connection cancelled")));
          return;
        }
      }, 250);
    };

    const accept = (address: `0x${string}`) => {
      rememberWallet(address);
      dismissWalletModal();
      finish(() => resolve(address));
    };
    // An installed wallet already linked to the address being replaced shows "already linked"
    // and never offers another account. Ask it for account permissions so the customer can pick one.
    let askedAccounts = false;
    const askForAnotherAccount = async () => {
      const provider = evmProvider(appkit);
      if (!provider) return;
      try {
        await requestProvider(provider, "wallet_requestPermissions", [{ eth_accounts: {} }]);
        const accounts = (await requestProvider(provider, "eth_accounts")) as unknown;
        const next = Array.isArray(accounts)
          ? accounts.find((item): item is `0x${string}` => typeof item === "string" && acceptedAddress(item, true, ignored))
          : undefined;
        if (next && !settled) accept(next);
      } catch {
        /* rejected or unsupported: the customer can still switch inside the wallet or close the window */
      }
    };
    const unsubAcc = appkit.subscribeAccount((state) => {
      if (!pickable(state.address, Boolean(state.isConnected))) return;
      if (!picked && untilClose && state.address?.toLowerCase() === (existing?.toLowerCase() || "")) return;
      accept(state.address as `0x${string}`);
    }, "eip155");

    const unsubState = appkit.subscribeState((state) => {
      if (state.open) {
        sawOpen = true;
        if (closeTimer) {
          window.clearTimeout(closeTimer);
          closeTimer = 0;
        }
        return;
      }
      if (sawOpen) cancelIfClosed();
    });
    poll = window.setInterval(() => {
      try {
        if (generation !== connectGeneration) {
          finish(() => reject(new Error("Wallet connection cancelled")));
          return;
        }
        // "Already linked" is AppKit's screen for an injected wallet that is already
        // authorized. Continue straight to the transaction; don't wait for eth_accounts
        // or for the customer to close the window.
        const view = String(RouterController.state.view || "");
        const topic = walletConnectTopic(appkit);
        if (view === "ConnectingExternal" || (topic && topic !== startTopic)) picked = true;
        if (picked) {
          const seen = currentAddress(appkit);
          if (pickable(seen, isConnected(appkit) || Boolean(seen))) {
            accept(seen);
            return;
          }
          const linked = String(currentAddress(appkit) || "").toLowerCase();
          if (view === "ConnectingExternal" && ignored && linked === ignored && !askedAccounts) {
            askedAccounts = true;
            void askForAnotherAccount();
          }
        }
        if (walletModalOpen()) {
          sawOpen = true;
          return;
        }
        // A dismissed modal must end the attempt even if AppKit never reported open.
        if (sawOpen) cancelIfClosed();
      } catch (error) {
        finish(() => reject(error instanceof Error ? error : new Error("Wallet connection cancelled")));
      }
    }, 200);
  });
}

export function prefetchWallet(): void {
  void ensureAppKit().catch(() => {
    /* project id / AppKit can fail until subscribe */
  });
}

export async function connectWallet(
  chainId: number,
  opts?: { switchWallet?: boolean; ignoreAddress?: string; chooseWallet?: boolean }
): Promise<`0x${string}`> {
  const network = networkById(chainId);
  if (!network) {
    throw new Error(`Unsupported chain ${chainId}`);
  }
  const appkit = await ensureAppKit(network);
  const ignoredAddress = (opts?.ignoreAddress || "").toLowerCase();
  // Same page, same wallet: the next step is the transaction, not another connect screen.
  if (pageWallet && pageWallet.toLowerCase() !== ignoredAddress) return pageWallet;
  const generation = ++connectGeneration;
  connectInFlight = null;

  const flight: { current: Promise<`0x${string}`> | null } = { current: null };
  const attempt = (async () => {
    try {
      // AppKit can restore a connection from an earlier visit. It is not this customer's pick until chosen in the list.
      const restored = isConnected(appkit) ? currentAddress(appkit) : null;
      await openConnectModal(appkit);
      if (generation !== connectGeneration) throw new Error("Wallet connection cancelled");
      if (!walletModalOpen()) throw new Error("Wallet window did not open");
      return await waitForConnection(
        appkit,
        ignoredAddress || null,
        generation,
        restored || ignoredAddress,
        Boolean(opts?.switchWallet)
      );
    } finally {
      if (connectInFlight === flight.current) connectInFlight = null;
    }
  })();
  flight.current = attempt;
  connectInFlight = attempt;
  return attempt;
}

export type WalletAccountOptions = {
  /** Continue only when the wallet's selected account is this address. */
  account?: string;
  /** Continue only when the selected account is a different one. */
  differentFrom?: string;
  /** Ask for a wallet on the first action of this page. A wallet already picked here is reused. */
  chooseWallet?: boolean;
  onStatus?: (message: string) => void;
  isCancelled?: () => boolean;
};

export async function useWalletAccount(chainId: number, opts?: WalletAccountOptions): Promise<`0x${string}`> {
  const network = networkById(chainId);
  if (!network) throw new Error(`Unsupported chain ${chainId}`);
  if (opts?.isCancelled?.()) throw new Error("Wallet connection cancelled");
  const appkit = await ensureAppKit(network);
  const providerNow = () => evmProvider(appkit);
  const live = pageWallet;
  if (
    live &&
    !opts?.differentFrom &&
    (!opts?.account || sameAddress(live, opts.account)) &&
    !walletModalOpen()
  ) {
    return live;
  }

  if (opts?.account && live && !sameAddress(live, opts.account)) {
    opts.onStatus?.(`Switch to ${shortAddress(opts.account)} in your wallet. That address pays this subscription.`);
  }
  if (!opts?.account && !opts?.differentFrom && connectInFlight) {
    return connectInFlight;
  }

  if (opts?.differentFrom) {
    opts.onStatus?.("Choose a different wallet, or switch the account in MetaMask.");
  }
  const provider = providerNow();
  const watcher = opts?.differentFrom && provider
    ? watchSelectedAccount(
        provider,
        (address) => !sameAddress(address, opts.differentFrom) && !sameAddress(address, live),
        opts?.isCancelled
      )
    : opts?.account && live && !sameAddress(live, opts.account) && provider
      ? watchSelectedAccount(provider, (address) => sameAddress(address, opts.account), opts?.isCancelled)
      : null;
  let watcherWon = false;
  const connecting = connectWallet(
    chainId,
    opts?.differentFrom
      ? { switchWallet: true, ignoreAddress: opts.differentFrom }
      : opts?.account && live && !sameAddress(live, opts.account)
        ? { switchWallet: true, ignoreAddress: live }
        : undefined
  )
    .catch((error: unknown) => {
      if (watcherWon) return null;
      throw error;
    });

  let picked: `0x${string}` | null;
  try {
    if (watcher) {
      picked = await Promise.race([
        connecting,
        watcher.done.then((address) => {
          watcherWon = true;
          connectGeneration += 1;
          dismissWalletModal();
          return address;
        }),
      ]);
    } else {
      picked = await connecting;
    }
  } finally {
    watcher?.stop();
  }
  if (!picked) throw new Error("Wallet connection cancelled");
  if (opts?.isCancelled?.()) throw new Error("Wallet connection cancelled");

  const settled = picked;
  if (opts?.account && !sameAddress(settled, opts.account)) {
    const provider = providerNow();
    if (!provider) {
      throw new Error(`Switch to ${shortAddress(opts.account)} in your wallet. That address pays this subscription.`);
    }
    return promptPayerAccount(provider, opts.account, opts.onStatus, opts.isCancelled);
  }
  if (opts?.differentFrom && sameAddress(settled, opts.differentFrom)) {
    throw new Error("This wallet already pays the subscription. Connect a different one.");
  }
  return settled;
}

function tokenAmountRaw(amount: string, decimals: number): bigint {
  const cleaned = amount.replace(",", ".").trim();
  if (!/^\d+(\.\d+)?$/.test(cleaned)) return 0n;
  const [whole, frac = ""] = cleaned.split(".");
  const digits = frac.slice(0, decimals);
  const fracRaw = digits ? BigInt(digits.padEnd(decimals, "0")) : 0n;
  return BigInt(whole) * 10n ** BigInt(decimals) + fracRaw;
}

async function rpcCall(chainId: number, method: string, params: unknown[]): Promise<string> {
  const rpc = ADD_CHAIN[chainId]?.rpcUrls[0];
  if (!rpc) throw new Error("This network has no public RPC");
  const response = await fetch(rpc, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: 1, method, params }),
  });
  const body = await response.json();
  if (!body || body.error || typeof body.result !== "string") {
    throw new Error(body?.error?.message || "Balance check failed");
  }
  return body.result;
}

/** Native coin must cover the approve gas, and the selected token must cover the first charge. */
async function assertActivationFunds(opts: {
  chainId: number;
  from: string;
  token: string;
  spender: string;
  amountRaw: bigint;
  paymentRaw: bigint;
  tokenSymbol: string;
  purpose?: string;
  /** False when the allowance already covers the cap: no approve, so no gas is needed. */
  approve?: boolean;
}): Promise<void> {
  const short: string[] = [];
  const nativeSymbol = ADD_CHAIN[opts.chainId]?.nativeCurrency.symbol || "ETH";
  if (opts.approve !== false) {
    try {
      const balance = BigInt(await rpcCall(opts.chainId, "eth_getBalance", [opts.from, "latest"]));
      const data = ERC20_APPROVE.encodeFunctionData("approve", [opts.spender, opts.amountRaw]);
      let gas = 100_000n;
      try {
        gas = BigInt(
          await rpcCall(opts.chainId, "eth_estimateGas", [
            { from: opts.from, to: opts.token, data, value: "0x0" },
          ])
        );
      } catch {
        /* a node that cannot estimate still needs a gas budget */
      }
      const price = BigInt(await rpcCall(opts.chainId, "eth_gasPrice", []));
      const need = (gas * price * 3n) / 2n;
      if (price > 0n && balance < need) short.push(nativeSymbol);
    } catch {
      /* an unread native balance must not block a wallet we could not check */
    }
  }
  if (opts.paymentRaw > 0n) {
    try {
      const data = ERC20_APPROVE.encodeFunctionData("balanceOf", [opts.from]);
      const raw = await rpcCall(opts.chainId, "eth_call", [{ to: opts.token, data }, "latest"]);
      if (BigInt(raw) < opts.paymentRaw) short.push(opts.tokenSymbol || "the selected token");
    } catch {
      /* an unread token balance must not block a wallet we could not check */
    }
  }
  if (short.length) throw new InsufficientFundsError(short, opts.purpose);
}

export async function connectAndApprove(opts: {
  chainId: number;
  token: string;
  spender: string;
  amountRaw: bigint;
  account?: string;
  differentFrom?: string;
  chooseWallet?: boolean;
  /** Sign out of the current wallet and pick one again from the list. */
  freshWallet?: boolean;
  /** One charge of the selected token. Set on activation so a short wallet never sends the approve. */
  activation?: { paymentAmount: string; tokenDecimals: number; tokenSymbol: string; purpose?: string };
  /** Allowance the wallet's other subscriptions still need. approve replaces the allowance, so it must cover them too. */
  otherBudgets?: (payer: `0x${string}`) => Promise<bigint>;
  /** Signed subscription terms (EIP-712), fetched for the connected wallet and signed before any approve. */
  terms?: (payer: `0x${string}`) => Promise<MandateDraft>;
  /** Send the approve only when the current allowance is below the amount. `tx` is then empty. */
  approveIfNeeded?: boolean;
  onStatus?: (message: string) => void;
  /** The wallet is picked and AppKit's connect window is closed; later statuses are wallet prompts. */
  onConnected?: (payer: `0x${string}`) => void;
  isCancelled?: () => boolean;
}): Promise<{ payer: `0x${string}`; tx: string; signature?: string }> {
  const network = networkById(opts.chainId);
  if (!network) {
    throw new Error(`Unsupported chain ${opts.chainId}`);
  }
  const appkit = await ensureAppKit(network);
  try {
    let differentFrom = opts.differentFrom;
    if (opts.freshWallet) {
      // Disconnect can wait on the wallet forever; give it a moment, then open the list anyway.
      await Promise.race([signOutWallet(), sleep(2000)]);
      const stale = currentAddress(appkit);
      if (stale && isConnected(appkit)) differentFrom = differentFrom || stale;
    }
    if (pageWallet && !(evmProvider(appkit) && sameAddress(currentAddress(appkit), pageWallet))) {
      // The remembered wallet has no live session in this browser; ask the customer to connect it again.
      pageWallet = null;
    }
    const payer = await useWalletAccount(opts.chainId, { ...opts, differentFrom });
    if (opts.isCancelled?.()) throw new Error("Wallet connection cancelled");
    dismissWalletModal();
    const from = payer;
    if (opts.account && !sameAddress(from, opts.account)) {
      throw new Error(`Switch to ${shortAddress(opts.account)} in your wallet. That address pays this subscription.`);
    }
    if (differentFrom && sameAddress(from, differentFrom)) {
      throw new Error("This wallet already pays the subscription. Connect a different one.");
    }
    opts.onConnected?.(from);
    const status = pacedStatus(opts.onStatus);
    let amountRaw = opts.amountRaw;
    if (opts.otherBudgets || opts.approveIfNeeded || opts.activation) await status("Checking your wallet…");
    if (opts.otherBudgets) {
      try {
        amountRaw += await opts.otherBudgets(from);
      } catch {
        throw new Error("Could not check the other subscriptions on this wallet. Try again.");
      }
    }
    let allowed = 0n;
    if (amountRaw > 0n) {
      try {
        allowed = await readAllowance({ chainId: opts.chainId, token: opts.token, owner: from, spender: opts.spender });
      } catch {
        throw new Error("Could not check the token allowance. Try again.");
      }
    }
    let needsApprove = true;
    if (opts.approveIfNeeded) {
      needsApprove = amountRaw > 0n && allowed < amountRaw;
    }
    if (opts.activation && opts.amountRaw > 0n) {
      await assertActivationFunds({
        chainId: opts.chainId,
        from,
        token: opts.token,
        spender: opts.spender,
        amountRaw,
        paymentRaw: tokenAmountRaw(opts.activation.paymentAmount, opts.activation.tokenDecimals),
        tokenSymbol: opts.activation.tokenSymbol,
        purpose: opts.activation.purpose,
        approve: needsApprove,
      });
    }
    const provider = evmProvider(appkit) || (await waitForProvider(appkit));
    await ensureChain(appkit, provider, network);
    let signature: string | undefined;
    if (opts.terms) {
      await status("Preparing the subscription terms…");
      const draft = await opts.terms(from);
      if (opts.isCancelled?.()) throw new Error("Wallet connection cancelled");
      await status(`Sign the subscription terms in your wallet\n${draft.terms}`);
      signature = await signTerms(provider, from, draft.typed_data);
    }
    if (!needsApprove) return { payer: from, tx: "", signature };
    let reset = false;
    if (allowed > 0n && (await mustResetFirst(provider, opts.chainId, from, opts.token, opts.spender, amountRaw))) {
      // Tokens such as USDT revert when approve replaces a non-zero allowance. Reset it first.
      const others = amountRaw > opts.amountRaw;
      await status(others ? `Reset the token allowance in your wallet\n${RESET_WARNING}` : "Reset the token allowance in your wallet");
      const resetTx = await sendApprove(provider, from, opts.token, opts.spender, 0n, opts.chainId);
      reset = true;
      await status("Waiting for the allowance reset…");
      await waitForReceipt(opts.chainId, resetTx, "Allowance reset");
    }
    await status("Confirm the approval in your wallet");
    let hash: string;
    try {
      hash = await sendApprove(provider, from, opts.token, opts.spender, amountRaw, opts.chainId);
    } catch (error) {
      // Saves that accept no approve hash can go on if the approval reached the chain anyway.
      if (opts.activation || opts.approveIfNeeded) {
        await status("Checking whether the approval went through…");
        const arrived = await allowanceArrived(
          () => readAllowance({ chainId: opts.chainId, token: opts.token, owner: from, spender: opts.spender }),
          amountRaw,
          lateApproveWaitMs(providerErrorCode(error))
        );
        if (arrived) return { payer: from, tx: "", signature };
      }
      throw reset ? afterResetError(error) : error;
    }
    return { payer: from, tx: hash, signature };
  } finally {
    if (!opts.isCancelled?.()) dismissWalletModal();
  }
}

/** Each status stays at least `minMs` before the next replaces it, so quick checks do not flicker.
 * The flow waits too, so the wallet never opens while the page still shows an earlier step. */
function pacedStatus(onStatus?: (message: string) => void, minMs = 1000): (message: string) => Promise<void> {
  let shownAt = 0;
  let last = "";
  return async (message) => {
    if (!onStatus || message === last) return;
    const wait = shownAt + minMs - Date.now();
    if (shownAt && wait > 0) await sleep(wait);
    onStatus(message);
    shownAt = Date.now();
    last = message;
  };
}

/** The wallet must already be on the terms' chain: wallets refuse typed data for another chainId. */
async function signTerms(provider: any, from: string, typedData: unknown): Promise<string> {
  let signature: unknown;
  try {
    signature = await requestProvider(provider, "eth_signTypedData_v4", [from, JSON.stringify(typedData)]);
  } catch (error) {
    if (providerErrorCode(error) === 4001) throw new Error("The subscription terms were not signed");
    throw error;
  }
  if (typeof signature !== "string" || !signature.startsWith("0x")) {
    throw new Error("Wallet did not return a signature");
  }
  return signature;
}

export async function revokeAllowance(opts: {
  chainId: number;
  token: string;
  spender: string;
  account?: string;
  /** What the wallet's other subscriptions still need; the allowance drops to that instead of zero. */
  otherBudgets?: (payer: `0x${string}`) => Promise<bigint>;
  onStatus?: (message: string) => void;
  onConnected?: (payer: `0x${string}`) => void;
  isCancelled?: () => boolean;
}): Promise<string> {
  const sent = await connectAndApprove({
    chainId: opts.chainId,
    token: opts.token,
    spender: opts.spender,
    amountRaw: 0n,
    account: opts.account,
    otherBudgets: opts.otherBudgets,
    onStatus: opts.onStatus,
    onConnected: opts.onConnected,
    isCancelled: opts.isCancelled,
  });
  opts.onStatus?.("Waiting for blockchain confirmation…");
  await waitForReceipt(opts.chainId, sent.tx, "Revoke");
  return sent.tx;
}

export async function approveAmount(opts: {
  chainId: number;
  token: string;
  spender: string;
  amountRaw: bigint;
  account?: string;
  differentFrom?: string;
  onStatus?: (message: string) => void;
  isCancelled?: () => boolean;
}): Promise<string> {
  const sent = await connectAndApprove(opts);
  await waitForReceipt(opts.chainId, sent.tx, "Approve");
  return sent.tx;
}

export async function readAllowance(opts: {
  chainId: number;
  token: string;
  owner: string;
  spender: string;
}): Promise<bigint> {
  const rpc = ADD_CHAIN[opts.chainId]?.rpcUrls[0];
  if (!rpc) throw new Error("This network has no public RPC");
  const data = ERC20_APPROVE.encodeFunctionData("allowance", [opts.owner, opts.spender]);
  const response = await fetch(rpc, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({
      jsonrpc: "2.0",
      id: 1,
      method: "eth_call",
      params: [{ to: opts.token, data }, "latest"],
    }),
  });
  const body = (await response.json()) as { result?: string; error?: { message?: string } };
  if (!body.result || body.result === "0x") {
    if (body.error) throw new Error(body.error.message || "Could not read allowance");
    return 0n;
  }
  return BigInt(body.result);
}

async function sendApprove(
  provider: any,
  from: string,
  token: string,
  spender: string,
  amount: bigint,
  chainId: number
): Promise<string> {
  const data = ERC20_APPROVE.encodeFunctionData("approve", [spender, amount]);
  let hash: unknown;
  try {
    hash = await requestProvider(provider, "eth_sendTransaction", [
      { from, to: token, data, value: "0x0", chainId: hexChainId(chainId) },
    ]);
  } catch (error) {
    if (providerErrorCode(error) === 4001) {
      // Keep the code: a plain "no" needs no wait for a late approval.
      throw Object.assign(new Error("The approval was rejected in the wallet"), { code: 4001 });
    }
    throw error;
  }
  if (typeof hash !== "string" || !hash.startsWith("0x")) {
    throw new Error("Wallet did not return a transaction hash");
  }
  return hash;
}

/** Whether this approve has to go through 0 first. Only a listed token or a real revert says so. */
async function mustResetFirst(
  provider: any,
  chainId: number,
  from: string,
  token: string,
  spender: string,
  amount: bigint
): Promise<boolean> {
  if (needsZeroFirst(chainId, token)) return true;
  const data = ERC20_APPROVE.encodeFunctionData("approve", [spender, amount]);
  try {
    await requestProvider(provider, "eth_estimateGas", [{ from, to: token, data, value: "0x0" }]);
    return false;
  } catch (error) {
    return isRevertError(error);
  }
}

async function waitForReceipt(chainId: number, hash: string, label: string): Promise<void> {
  const started = Date.now();
  while (Date.now() - started < 90_000) {
    const receipt = await readReceipt(chainId, hash);
    if (receipt) {
      const status = receipt.status;
      if (status === "0x0" || status === 0) {
        throw new Error(`${label} transaction failed`);
      }
      return;
    }
    await sleep(1500);
  }
  throw new Error(`${label} transaction is still pending. Try again in a moment.`);
}

async function readReceipt(chainId: number, hash: string): Promise<{ status?: string | number } | null> {
  const rpc = ADD_CHAIN[chainId]?.rpcUrls[0];
  if (!rpc) return null;
  try {
    const response = await fetch(rpc, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        jsonrpc: "2.0",
        id: 1,
        method: "eth_getTransactionReceipt",
        params: [hash],
      }),
    });
    const body = (await response.json()) as { result?: { status?: string | number } | null };
    return body.result || null;
  } catch {
    return null;
  }
}
